import * as Bixby from '$/core';
import { CacheService } from './CacheService';
import decode from 'jwt-decode';

export abstract class TokenService {
  // these min/max values are to help us deal with the client's clock being off and hence not trustworthy when comparing to nbf/exp from the token
  private static readonly tokenRefreshSafetyMarginMinutes: number = 1; // number of minutes before expiration to do the refresh
  private static readonly refreshTokenTimeoutMinutes: number = 7 * 24 * 60;
  private static readonly originalTokenTimeoutMinutes: number = 60;
  private static refreshInterval: NodeJS.Timeout;

  public static isAuthenticated(): boolean {
    const access_token: string | null = TokenService.getAccessToken();
    return (access_token || '').length > 0;
  }

  public static getAccessToken(): string | null {
    const access_token: string | null = CacheService.get<string>(Bixby.Constants.CACHE_BIXBY_ACCESS_TOKEN);
    return access_token;
  }

  private static getRefreshToken(): string | null {
    const refresh_token: string | null = CacheService.get<string>(Bixby.Constants.CACHE_BIXBY_REFRESH_TOKEN);
    return refresh_token;
  }

  public static decodeToken(token: string): Bixby.Models.DecodedToken | undefined {
    if (!token) {
      return undefined;
    }

    try {
      const decoded: Bixby.Models.DecodedToken = decode(token) as Bixby.Models.DecodedToken;

      if (Bixby.Utils.isString(decoded.role)) {
        decoded.role = [decoded.role.toString()];
      }

      return decoded;
    } catch {
      return undefined;
    }
  }

  public static getDecodedAccessToken(): Bixby.Models.DecodedToken | undefined {
    const access_token: string | null = TokenService.getAccessToken();

    if (!access_token) {
      return undefined;
    }

    return TokenService.decodeToken(access_token);
  }

  public static getUserId(): string | undefined {
    return (TokenService.getDecodedAccessToken() || { sub: undefined }).sub;
  }

  public static setTokens(openIdResponse: Bixby.Models.OpenIdResponse): void {
    // set the token
    let accessTokenExpirationMinutes: number = TokenService.originalTokenTimeoutMinutes;
    let refreshTokenExpirationMinutes: number = TokenService.refreshTokenTimeoutMinutes;

    if (openIdResponse) {
      if (openIdResponse.expires_in) {
        accessTokenExpirationMinutes = openIdResponse.expires_in / TokenService.originalTokenTimeoutMinutes;
      } else {
        const decodedAccessToken: Bixby.Models.DecodedToken | undefined = TokenService.decodeToken(
          openIdResponse.access_token
        );
        accessTokenExpirationMinutes = (decodedAccessToken && this.getTokenRemainingMinutes(decodedAccessToken)) || 60;
      }

      const decodedRefreshToken: Bixby.Models.DecodedToken | undefined = TokenService.decodeToken(
        openIdResponse.refresh_token
      );
      refreshTokenExpirationMinutes =
        (decodedRefreshToken && this.getTokenRemainingMinutes(decodedRefreshToken)) ||
        TokenService.refreshTokenTimeoutMinutes;
    }

    CacheService.set(
      Bixby.Constants.CACHE_BIXBY_ACCESS_TOKEN,
      openIdResponse.access_token,
      accessTokenExpirationMinutes
    );
    CacheService.set(
      Bixby.Constants.CACHE_BIXBY_REFRESH_TOKEN,
      openIdResponse.refresh_token,
      refreshTokenExpirationMinutes
    );

    // clear the refresh
    if (TokenService.refreshInterval) {
      clearInterval(TokenService.refreshInterval);
    }

    // start the timer again
    this.setRefreshInterval(accessTokenExpirationMinutes - TokenService.tokenRefreshSafetyMarginMinutes); // refresh a bit before it expires
  }

  public static clearTokens(): void {
    // set the token
    CacheService.remove(Bixby.Constants.CACHE_BIXBY_ACCESS_TOKEN);
    CacheService.remove(Bixby.Constants.CACHE_BIXBY_REFRESH_TOKEN);

    // clear the refresh
    if (TokenService.refreshInterval) {
      clearInterval(TokenService.refreshInterval);
    }
  }

  private static getTokenRemainingMinutes(token?: Bixby.Models.DecodedToken): number {
    if (!token) {
      return 0;
    }

    return (token.exp - Math.max(token.nbf, new Date().getTime() / 1000)) / 60;
  }

  private static setRefreshInterval(interval: number): void {
    // eslint-disable-next-line no-console
    console.log(new Date() + ': Preparing for token refresh interval of ' + interval);

    if (interval <= 0) {
      // eslint-disable-next-line no-console
      console.log(new Date() + ': Invalid interval ' + interval);
    } else {
      if (TokenService.refreshInterval) {
        clearInterval(TokenService.refreshInterval);
      }

      const intervalInternal: number = interval * 60 * 1000;
      TokenService.refreshInterval = setInterval(async () => {
        const refresh_token: string | null = TokenService.getRefreshToken();
        if (!refresh_token) {
          // eslint-disable-next-line no-console
          console.log(new Date() + ': Invalid refresh token');
        } else {
          // eslint-disable-next-line no-console
          console.log(new Date() + ': Refreshing Token...');
          const route = Bixby.API.ServerRoute.forAction('connect', 'token');
          const openIdRequest: Bixby.Models.OpenIdRequest & { [key: string]: string } = {
            grant_type: 'refresh_token',
            refresh_token: refresh_token,
            client_id: Bixby.Configuration.clientId,
            client_secret: Bixby.Configuration.clientSecret,
          };
          const config = {
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
          };
          const params = new URLSearchParams();
          Object.keys(openIdRequest).forEach(key => params.append(key, openIdRequest[key]));

          try {
            const openIdResponse: Bixby.Models.OpenIdResponse =
              await Bixby.API.postAnonymous<Bixby.Models.OpenIdResponse>(route, params.toString(), config);

            // if we don't get a token back (but we got here, which means we didn't get a 401/403 error),
            //   we must be using a non-renewable token - we'll just use it until it starts giving us errors
            if (openIdResponse && openIdResponse.access_token) {
              // store token
              TokenService.setTokens(openIdResponse);
            } else {
              // eslint-disable-next-line no-console
              console.log(new Date() + ': Unable to refresh token (no token returned)');
            }
          } catch (e: any) {
            // eslint-disable-next-line no-console
            console.log(new Date() + ': Unable to refresh token', e);
          }
        }
      }, intervalInternal);
    }
  }

  public static resumeFromCache(): boolean {
    const access_token: string | null = CacheService.get<string>(Bixby.Constants.CACHE_BIXBY_ACCESS_TOKEN);

    if (access_token) {
      const decodedAccessToken: Bixby.Models.DecodedToken | undefined = TokenService.decodeToken(access_token);
      const accessTokenExpirationMinutes: number =
        (decodedAccessToken && this.getTokenRemainingMinutes(decodedAccessToken)) ||
        TokenService.originalTokenTimeoutMinutes;

      // clear the refresh
      if (TokenService.refreshInterval) {
        clearInterval(TokenService.refreshInterval);
      }

      // start the timer again
      this.setRefreshInterval(accessTokenExpirationMinutes - TokenService.tokenRefreshSafetyMarginMinutes); // refresh a bit before it expires

      return true;
    } else {
      TokenService.clearTokens();

      return false;
    }
  }
}
